// @ts-check
/**
 * src/api/stepUp.js
 *
 * §16 step-up authentication — the browser half (server half:
 * azure-functions/shared/stepUp.js, azure-functions/step-up-verify).
 *
 * Holds the short-lived `stepUpToken` IN MEMORY ONLY — never localStorage,
 * sessionStorage or a cookie. A reload drops it and the next gated action asks
 * again, which is the intended cost: anything persisted would outlive the
 * 10-minute window's purpose and be readable by any script on the origin.
 *
 * Leaf module (no imports): azureClient.js attaches the token and, on a
 * `403 { stepUp: 'required' }`, calls requestStepUp(), which asks whatever
 * dialog registered itself (StepUpProvider) to run the re-verification.
 */

/** @type {string | null} */
let _token = null;
let _expiresAtMs = 0;

/** Refuse to send a token this close to expiry — the server would 403 it mid-flight. */
const EXPIRY_MARGIN_MS = 5000;

/**
 * @param {string} token
 * @param {string} expiresAt ISO timestamp from step-up-verify
 * @returns {void}
 */
export function setStepUpToken(token, expiresAt) {
  const ms = Date.parse(expiresAt);
  if (!token || Number.isNaN(ms)) return;
  _token = token;
  _expiresAtMs = ms;
}

/** @returns {string | null} the current token, or null when absent or about to expire */
export function getStepUpToken() {
  if (_token && Date.now() < _expiresAtMs - EXPIRY_MARGIN_MS) return _token;
  _token = null;
  _expiresAtMs = 0;
  return null;
}

/** @returns {void} */
export function clearStepUpToken() {
  _token = null;
  _expiresAtMs = 0;
}

// ── The dialog bridge ──────────────────────────────────────────────────────

/** @type {(() => Promise<boolean>) | null} */
let _handler = null;
/** @type {Promise<boolean> | null} */
let _pending = null;

/**
 * Register the function that opens the re-verify dialog. It resolves true once
 * a token has been stored, false when the user cancelled.
 *
 * @param {() => Promise<boolean>} fn
 * @returns {() => void} unregister
 */
export function registerStepUpHandler(fn) {
  _handler = fn;
  return () => {
    if (_handler === fn) _handler = null;
  };
}

/**
 * Ask the user to re-verify. Concurrent gated calls share one dialog.
 * Resolves false when no dialog is mounted (e.g. a surface without the
 * provider) — the caller then surfaces the refusal as an ordinary 403.
 *
 * @returns {Promise<boolean>}
 */
export function requestStepUp() {
  if (!_handler) return Promise.resolve(false);
  if (_pending) return _pending;
  const handler = _handler;
  _pending = Promise.resolve()
    .then(() => handler())
    .then(
      (ok) => !!ok,
      () => false
    )
    .finally(() => {
      _pending = null;
    });
  return _pending;
}

/**
 * True when a response is the gate's refusal (`403 { stepUp: 'required' }`).
 * Reads a clone, so the caller still holds an unconsumed body.
 *
 * @param {Response} res
 * @returns {Promise<boolean>}
 */
export async function isStepUpRequired(res) {
  if (!res || res.status !== 403) return false;
  try {
    const body = await res.clone().json();
    return body?.stepUp === 'required';
  } catch {
    return false;
  }
}

/** Error thrown when the user dismisses the re-verify dialog. */
export const STEP_UP_CANCELLED = 'Re-verification cancelled';
